/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useContext, lazy } from 'react';
import * as Sentry from '@sentry/react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { getRoutes, grp, RouteId } from './routes';
import ScrollToTop from './helpers/scroll-to-top';
import { AuthContext } from './contexts/auth';
import { EmptyLayout, AppShellLayout } from './layouts';
import { LogoutPage } from './pages/logout'; // Logout process'inde gecikme olmaması için lazy yapılmadı.

const LoginPage = lazy(() => import('./pages/login'));
const RegisterPage = lazy(() => import('./pages/register'));
const ConfirmPage = lazy(() => import('./pages/confirm'));
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password'));
const AccountPage = lazy(() => import('./pages/account'));
const ProjectSettingsPage = lazy(() => import('./pages/project-settings'));
const DepartmentListPage = lazy(() => import('./pages/departments'));
const DepartmentAddPage = lazy(() => import('./pages/department-add'));
const DepartmentUpdatePage = lazy(() => import('./pages/department-update'));
const DepartmentDetailPage = lazy(() => import('./pages/department-detail'));
const EmployeeListPage = lazy(() => import('./pages/employees'));
const EmployeeAddPage = lazy(() => import('./pages/employee-add'));
const EmployeeUpdatePage = lazy(() => import('./pages/employee-update'));
const EmployeeDetailPage = lazy(() => import('./pages/employee-detail'));
const DeviceListPage = lazy(() => import('./pages/devices'));
const DeviceDetailPage = lazy(() => import('./pages/device-detail'));
const UsedApplicationListPage = lazy(() => import('./pages/used-applications'));
const UsedApplicationDetailPage = lazy(() => import('./pages/used-application-detail'));
const OverviewPage = lazy(() => import('./pages/overview'));
const VendorListPage = lazy(() => import('./pages/vendors'));
const VendorDetailPage = lazy(() => import('./pages/vendor-detail'));
const VendorAddPage = lazy(() => import('./pages/vendor-add'));
const VendorUpdatePage = lazy(() => import('./pages/vendor-update'));
const IntegrationsPage = lazy(() => import('./pages/integrations'));
const LicensesPage = lazy(() => import('./pages/licenses'));
const LicenseAddPage = lazy(() => import('./pages/license-add'));
const LicenseUpdatePage = lazy(() => import('./pages/license-update'));
const LicenseDetailPage = lazy(() => import('./pages/license-detail'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const routeElements: Record<RouteId, JSX.Element> = {
  homepage: <OverviewPage />,
  login: <LoginPage />,
  logout: <LogoutPage />,
  register: <RegisterPage />,
  confirm: <ConfirmPage />,
  'forgot-password': <ForgotPasswordPage />,
  account: <AccountPage />,
  'project-settings': <ProjectSettingsPage />,
  departments: <DepartmentListPage />,
  'department-add': <DepartmentAddPage />,
  'department-detail': <DepartmentDetailPage />,
  'department-update': <DepartmentUpdatePage />,
  employees: <EmployeeListPage />,
  'employee-add': <EmployeeAddPage />,
  'employee-detail': <EmployeeDetailPage />,
  'employee-update': <EmployeeUpdatePage />,
  applications: <UsedApplicationListPage />,
  'application-detail': <UsedApplicationDetailPage />,
  devices: <DeviceListPage />,
  'device-detail': <DeviceDetailPage />,
  vendors: <VendorListPage />,
  'vendor-detail': <VendorDetailPage />,
  'vendor-add': <VendorAddPage />,
  'vendor-update': <VendorUpdatePage />,
  integrations: <IntegrationsPage />,
  licenses: <LicensesPage />,
  'license-detail': <LicenseDetailPage />,
  'license-add': <LicenseAddPage />,
  'license-update': <LicenseUpdatePage />,
};

const RequireAuth: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { authState } = useContext(AuthContext);
  if (!authState.isAuth) {
    return <Navigate to={grp('login')} />;
  }
  return <>{children}</>;
};

const RequireGuest: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { authState } = useContext(AuthContext);
  if (authState.isAuth) {
    return <Navigate to={grp('homepage')} />;
  }
  return <>{children}</>;
};

export const Router: React.FC = () => (
  <>
    <ScrollToTop />

    <SentryRoutes>
      <Route
        element={(
          <RequireGuest>
            <EmptyLayout />
          </RequireGuest>
        )}
      >
        {getRoutes().filter((route) => route.authRequired === false).map((route) => (
          <Route key={route.id} path={route.path} element={routeElements[route.id]} />
        ))}
      </Route>
      <Route
        element={(
          <RequireAuth>
            <AppShellLayout />
          </RequireAuth>
        )}
      >
        {getRoutes().filter((route) => route.authRequired === true).map((route) => (
          <Route key={route.id} path={route.path} element={routeElements[route.id]} />
        ))}
      </Route>
    </SentryRoutes>
  </>
);
